import { FC, useState } from 'react'
import toast from 'react-hot-toast'
import { getErrorFromCatch } from '../../api/api'
import { IShippingCarrierForm, shippingCarrierApi } from '../../api/shippingCarrier.api'
import { icons } from '../../icons/IconSet'
import { IShippingCarrier } from '../../model/IShippingCarrier'
import Modal from '../common/modal/Modal'
import Table, { ITableTh } from '../common/table/Table'
import Button from '../form/Button'
import ShippingCarrierForm from './ShippingCarrierForm'

const thead: ITableTh[] = [
  { title: 'ID' },
  { title: 'Name' },
  { title: 'URL' },
  { title: 'Tracking URL' },
  { title: 'Email' },
  { title: 'Phone' },
  { title: 'Visible' },
  { title: 'Actions', align: 'right' },
]

const ShippingCarrierList: FC = () => {
  const { data: carriers, isFetching } = shippingCarrierApi.useGetShippingCarriersQuery()
  const [createShippingCarrier, { isLoading: isCreating }] =
    shippingCarrierApi.useCreateShippingCarrierMutation()
  const [updateShippingCarrier, { isLoading: isUpdating }] =
    shippingCarrierApi.useUpdateShippingCarrierMutation()
  const [deleteShippingCarrier, { isLoading: isDeleting }] =
    shippingCarrierApi.useDeleteShippingCarrierMutation()

  const [isFormShown, setIsFormShown] = useState<boolean>(false)
  const [editing, setEditing] = useState<IShippingCarrier | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<IShippingCarrier | null>(null)

  const isSaving = isCreating || isUpdating

  const openCreate = () => {
    setEditing(null)
    setIsFormShown(true)
  }

  const openEdit = (carrier: IShippingCarrier) => {
    setEditing(carrier)
    setIsFormShown(true)
  }

  const closeForm = () => {
    setIsFormShown(false)
    setEditing(null)
  }

  const handleSubmit = async (form: IShippingCarrierForm) => {
    try {
      if (editing) {
        await updateShippingCarrier({ id: editing.id, ...form }).unwrap()
        toast.success('Shipping carrier updated')
      } else {
        await createShippingCarrier(form).unwrap()
        toast.success('Shipping carrier created')
      }
      closeForm()
    } catch (err) {
      toast.error(getErrorFromCatch(err) || 'Failed to save shipping carrier')
    }
  }

  const handleDelete = async () => {
    if (!deleteTarget) return
    try {
      await deleteShippingCarrier(deleteTarget.id).unwrap()
      toast.success('Shipping carrier deleted')
      setDeleteTarget(null)
    } catch (err) {
      toast.error(getErrorFromCatch(err) || 'Failed to delete shipping carrier')
    }
  }

  return (
    <div className='flex flex-col gap-1 w-full h-full'>
      <div className='bg-bg_light items-center border-border_color border-b-[1px] p-4'>
        <Button
          isNotFullWidth
          type='submit'
          size='small'
          className='whitespace-nowrap flex items-center'
          onClick={openCreate}
        >
          {icons('plus', { width: '16px' })}
          Add Carrier
        </Button>
      </div>

      <div className='flex-1'>
        <Table
          isPending={isFetching}
          isDisablePaginate
          thead={thead}
          tbody={(carriers || []).map((carrier) => ({
            data: [
              carrier.id,
              carrier.name,
              carrier.url,
              carrier.trackingUrl,
              carrier.email,
              carrier.phone,
              carrier.visible ? 'Yes' : 'No',
              <div className='flex gap-2 justify-end'>
                <Button
                  isNotFullWidth
                  type='button'
                  size='small'
                  onClick={() => openEdit(carrier)}
                >
                  {icons('edit_record', { width: '16px', fill: '#000' })}
                </Button>
                <Button
                  isNotFullWidth
                  type='delete'
                  variant='outline'
                  size='small'
                  onClick={() => setDeleteTarget(carrier)}
                >
                  {icons('x', { width: '16px' })}
                </Button>
              </div>,
            ],
          }))}
        />
      </div>

      {isFormShown && (
        <Modal
          isShown={isFormShown}
          title={editing ? 'Edit Shipping Carrier' : 'New Shipping Carrier'}
          style={{ maxWidth: '650px', overflowY: 'visible' }}
          onClose={closeForm}
        >
          <ShippingCarrierForm carrier={editing} loading={isSaving} onSubmit={handleSubmit} />
        </Modal>
      )}

      {!!deleteTarget && (
        <Modal
          isShown={!!deleteTarget}
          title='Delete Shipping Carrier'
          style={{ maxWidth: '480px' }}
          onClose={() => setDeleteTarget(null)}
        >
          <div className='flex flex-col gap-6'>
            <div>
              Are you sure you want to delete <b>{deleteTarget.name}</b>?
            </div>
            <div className='flex justify-end gap-3'>
              <Button
                isNotFullWidth
                type='button'
                disabled={isDeleting}
                onClick={() => setDeleteTarget(null)}
              >
                Cancel
              </Button>
              <Button
                isNotFullWidth
                type='delete'
                variant='outline'
                disabled={isDeleting}
                onClick={handleDelete}
              >
                Delete
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}

export default ShippingCarrierList
